import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useProperty,
  usePropertyField,
  usePropertyAddressUrl,
  useCollectionBinding,
  useTokens,
  usePage,
} from '@vibesites/runtime/react';
import { EditableToken, EditableSlotGroup } from '@vibesites/runtime/react';
import basePagesData from '@vibesites/theme-via6/pages.json';
import childPagesData from '../pages.json';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { contrastRatio, relativeLuminance, getContrastTextColor } from '../utils/luminance';

// ============================================================================
// Pages data: shallow-merge parent (Via6 base) + child (Weidner overrides)
// ----------------------------------------------------------------------------
// The base via6 `Header` / `_HeaderBase` reads pages.json via a *static*
// `import pagesData from '../pages.json'`, which resolves to the parent theme's
// own pages.json and therefore never picks up child-theme overrides. This file
// is a fork of the base whose only behavioral change is to merge the Weidner
// child pages.json on top of the base before exposing the same `pagesData`
// symbol to the rest of the module. The merge mirrors the `mergePages`
// shallow-per-alias semantics in `vibesites-core/runtime/page-merge.mjs`
// (child fields win, parent fields fill in) and crucially preserves the
// `parent` field so navigation dropdown nesting works for Weidner-defined
// hierarchies (e.g. Features → Amenities, Pet Policy, Future Residents Club;
// Gallery & Tours → Virtual Tour).
// ============================================================================
// Type is intentionally `any` so the rest of the file (which `as`-casts this
// to `Record<string, PageEntry>` / similar shapes) continues to work unchanged
// — the base did the same thing implicitly via `import ... from '*.json'`.
const pagesData: any = (() => {
  const out: Record<string, Record<string, unknown>> = {};
  for (const [alias, data] of Object.entries(basePagesData as Record<string, Record<string, unknown>>)) {
    out[alias] = { ...data };
  }
  for (const [alias, data] of Object.entries(childPagesData as Record<string, Record<string, unknown>>)) {
    out[alias] = { ...out[alias], ...data };
  }
  // ------------------------------------------------------------------
  // Synthetic nav-only umbrella parents: "Find Your Home", "Features",
  // and "Gallery & Tours".
  //
  // None of these umbrellas exist as a real page in pages.json — not in
  // the base via6 theme (the page that was historically aliased
  // `gallery-tours` was renamed to `gallery` so the alias matches its
  // /gallery path), and not in Weidner's pages.json. They live here as
  // a JS-only construct purely to give the header hoverable dropdown
  // parents:
  //
  //   Find Your Home  → Floor Plans / Interactive Map
  //   Features        → Amenities / Pet Policy / Future Residents Club
  //   Gallery & Tours → Gallery / Schedule A Tour / Virtual Tour
  //
  // (Base via6 nests Interactive Map under Floor Plans itself — so the
  // top-level label was "Floor Plans" with only Interactive Map in the
  // dropdown. Weidner wants both pages as peers under "Find Your Home".)
  //
  // Each umbrella's children declare `parent: "<alias>"` in
  // pages.json. Without a parent entry visible in the merged pagesData
  // here, the buildNavLinks eligibility check
  // (`eligibleAliases.has(page.parent)`) fails and the children bubble
  // up to the top level — dissolving the dropdown grouping into a flat
  // row of links.
  //
  // The injection is intentionally *unconditional* (always overwrites
  // any existing `find-your-home` / `features` / `gallery-tours` entry)
  // rather than guarded by `if (!out[alias])`. The base theme could in
  // principle re-introduce any alias for an unrelated purpose later,
  // and the umbrella semantics here would still need to win — this is
  // the Weidner header, not a generic base header, and the dropdown is
  // a brand requirement.
  //
  // The injection is JS-only: SSG reads pages.json directly (not this
  // in-memory merge), so none of these aliases materialize as a real
  // route. NAV_HREF_OVERRIDES below redirects clicks on each umbrella
  // label to a real child route (find-your-home → /floor-plans,
  // features → /amenities, gallery-tours → /gallery), and we mirror
  // those URLs as `path` here for defense-in-depth in case the
  // override is ever removed.
  // ------------------------------------------------------------------
  out['find-your-home'] = {
    name: 'Find Your Home',
    path: '/floor-plans',
    position: 1,
    visible: true,
  };
  out.features = {
    name: 'Features',
    path: '/amenities',
    position: 2,
    visible: true,
  };
  out['gallery-tours'] = {
    name: 'Gallery & Tours',
    path: '/gallery',
    position: 3,
    visible: true,
  };
  return out;
})();

// ============================================================================
// Dynamic Navigation from pages.json
// ============================================================================

interface PageEntry {
  name: string;
  path: string;
  position: number;
  visible?: boolean;
  template?: string;
  noindex?: boolean;
  /** When set, this page is rendered as a sub-nav item under the parent
      page's nav entry (a dropdown on desktop, indented row on mobile). */
  parent?: string;
}

/** Page aliases that should never appear in the main navigation.
 *
 * Weidner overrides (kept OUT of the base via6 set so they DO show in nav):
 *   - `home` — the base hides it because the logo doubles as the home link;
 *     Weidner wants an explicit "Home" link in the nav row in addition to
 *     the logo (it's `position: 0` in pages.json so it sorts to the first
 *     slot, ahead of Find Your Home).
 *   - `schedule-a-tour` — base hides it because it has a dedicated CTA pill
 *     in the utility row; Weidner wants it ALSO as a dropdown item under
 *     "Gallery & Tours". The CTA pill is rendered from a separate slot
 *     group, not from this nav builder, so the two affordances coexist.
 */
const NAV_EXCLUDED_ALIASES = new Set([
  'privacy-policy',
  'sitemap',
  'accessibility',
  'apply-now',
  'thank-you',
  'resident-access',
  'hyperlocal',
  'reviews',
]);

/**
 * Per-alias header-nav `href` overrides.
 *
 * The base nav builder uses `page.path` as each link's `href`. For a small
 * number of aliases we want the header link to point somewhere OTHER than
 * the page's own route — these are "umbrella" landing pages whose top-level
 * nav entry exists only to host a dropdown of child links; the umbrella's
 * own route is a placeholder and the real content lives on the children.
 * Weidner's brand wants clicking the umbrella label to jump straight to the
 * primary child page rather than the placeholder:
 *
 *   find-your-home → /floor-plans  (vs. dummy /find-your-home)
 *   features       → /amenities    (vs. dummy /features)
 *   gallery-tours  → /gallery      (vs. dummy /gallery-tours)
 *
 * The umbrella pages themselves stay generated by SSG (so direct visits to
 * /features or /gallery-tours still work) and the dropdown children are
 * unaffected. `find-your-home` is JS-only (never an SSG route).
 *
 * This is intentionally a nav-only override — `parent`-based child nesting,
 * SEO routing, and sitemap entries all keep using `page.path` from
 * pages.json. Only the rendered `<a href>` in the header is rewritten.
 */
const NAV_HREF_OVERRIDES: Record<string, string> = {
  'find-your-home': '/floor-plans',
  features: '/amenities',
  'gallery-tours': '/gallery',
};

interface NavLink {
  label: string;
  href: string;
  /** Sub-nav items rendered as a dropdown on desktop and indented rows on
      mobile. Populated when other pages declare `parent: <this alias>`. */
  children?: NavLink[];
}

interface VirtualTour {
  virtual_tour_url?: string | null;
}

interface PageSection {
  slot: string;
}

/**
 * Returns true when the page identified by `alias` (or, as a fallback, by
 * `pathname`) has `HeroSolidBG` as its primary hero — i.e. an interior page
 * where the navigation overlays the solid-primary hero rather than a
 * photographic hero. Looking up by alias works during SSG (the runtime
 * passes `page.alias` through `usePage()`) so the resulting class is baked
 * into the static HTML and the nav text is correct on first paint.
 */
function isOverSolidHero(alias: string | undefined, pathname?: string): boolean {
  const pages = pagesData as Record<string, PageEntry>;
  let entry: PageEntry | undefined;
  if (alias && pages[alias]) {
    entry = pages[alias];
  } else if (pathname) {
    entry = Object.values(pages).find((p) => p.path === pathname);
    if (!entry) {
      const trimmed = pathname.endsWith('/') && pathname !== '/' ? pathname.slice(0, -1) : `${pathname}/`;
      entry = Object.values(pages).find((p) => p.path === trimmed);
    }
  }
  const sections = (entry as { sections?: PageSection[] } | undefined)?.sections;
  if (!sections || !sections.length) return false;
  const hero = sections.find((s) => s.slot !== 'SpecialOfferBanner');
  return hero?.slot === 'HeroSolidBG';
}

/**
 * Build navigation links from pages.json.
 * Only includes pages that are visible and not in the exclusion list.
 * Sorted by position. Pages declaring `parent: <alias>` are nested as
 * `children` of their parent entry rather than appearing at the top level.
 * Orphaned children (parent missing or hidden) fall back to the top level
 * so a page is never silently dropped from the nav.
 *
 * `hasInteractiveLeasing` controls the dynamic visibility of the
 * Interactive Map page — that page renders an interactive site-map widget
 * driven by one of two per-property providers: Engrain SightMap
 * (`custom_engrain_sightmap_url`) or ApartmentSync
 * (`custom_apartmentsync_javascript_id`). When neither field is set the
 * property has no real map, so the page is dropped from the nav.
 *
 * The page appears iff BOTH conditions hold:
 *   1. It is enabled on the (child) pages.json (`visible !== false`). The
 *      base via6 pages.json ships `visible: false`; the Weidner child
 *      pages.json overrides it to `visible: true`. So unlike earlier
 *      revisions — which let the provider condition OVERRIDE the visible
 *      flag entirely — the visible flag is now respected: a child that
 *      disables the page hides it regardless of provider config.
 *   2. A provider is configured (`hasInteractiveLeasing`).
 *
 * The alias check still runs as a dedicated branch (rather than falling
 * through to the generic `visible === false` filter below) so the visible
 * gate and the provider gate are evaluated together for this page.
 */
function buildNavLinks(hasVirtualTours: boolean, hasInteractiveLeasing: boolean): NavLink[] {
  const eligible = Object.entries(pagesData as Record<string, PageEntry>)
    .filter(([alias, page]) => {
      if (NAV_EXCLUDED_ALIASES.has(alias)) return false;
      if (page.template) return false;
      // Interactive-map gate — show iff the page is ENABLED on the child
      // pages.json (`visible !== false`) AND the property has an interactive
      // site-map provider configured (Engrain SightMap URL OR ApartmentSync
      // JavaScript ID — see `hasInteractiveLeasing`). The Weidner child
      // pages.json sets `visible: true`, flipping the base via6 `visible:
      // false` default; this explicit alias branch keeps the visible check
      // and the provider check together so both must pass.
      if (alias === 'interactive-map') return page.visible !== false && hasInteractiveLeasing;
      if (page.visible === false) return false;
      if (alias === 'virtual-tour' && !hasVirtualTours) return false;
      return true;
    })
    .sort(([, a], [, b]) => a.position - b.position);

  const eligibleAliases = new Set(eligible.map(([alias]) => alias));
  const linksByAlias = new Map<string, NavLink>();
  const topLevel: NavLink[] = [];

  // First pass: create a NavLink for every eligible page. `href` defaults
  // to the page's own path, but NAV_HREF_OVERRIDES lets a specific alias
  // redirect its nav link elsewhere (see the constant's docblock above —
  // currently "features" → "/amenities" so the placeholder Features page
  // never serves as a nav destination).
  for (const [alias, page] of eligible) {
    const href = NAV_HREF_OVERRIDES[alias] ?? page.path;
    linksByAlias.set(alias, { label: page.name, href });
  }

  // Second pass: attach children to parents, preserving sort order. Pages
  // whose parent isn't eligible (hidden / excluded / missing) bubble up to
  // the top level so we never lose a page from the nav.
  for (const [alias, page] of eligible) {
    const link = linksByAlias.get(alias)!;
    if (page.parent && eligibleAliases.has(page.parent) && page.parent !== alias) {
      const parentLink = linksByAlias.get(page.parent)!;
      (parentLink.children ??= []).push(link);
    } else {
      topLevel.push(link);
    }
  }

  return topLevel;
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef = {
  label: 'Logo',
  fields: {
    image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const applyNowLinkDef = {
  label: 'Apply Now Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultField: 'property_apply_now_url',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const residentAccessLinkDef = {
  label: 'Resident Access Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Qualifying Criteria PDF link — rendered to the left of the Apply Now /
// Resident Access pair in the utility row (Weidner brand: surface the
// rental qualifying criteria document directly from the header so
// prospective applicants can review it before clicking Apply Now). Default
// URL points at the Seattle market PDF hosted on Weidner's CDN; per-property
// editors can swap in a market-specific PDF via the slot field.
const qualifyingCriteriaLinkDef = {
  label: 'Qualifying Criteria Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue:
        'https://irp.cdn-website.com/c035667c/files/uploaded/Weidner_Qualifying_Criteria_Seattle.pdf',
    },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Qualifying Criteria' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const scheduleTourLinkDef = {
  label: 'Schedule Tour Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// Address row inside the mobile drawer. Promoted from a bare
// `header.full_address` content token + auto-generated Google Maps
// directions URL to a slot group so editors can pick the address text,
// the destination URL, and the link target independently — rather than
// having the URL forced to the property's `google_place_url` (or a
// synthesized `/maps/dir/?destination=...` fallback) with no override.
const addressLinkDef = {
  label: 'Address Link',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, Atlanta, GA 30313',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time (mobile-safe Google Maps URL built from place / business / full
    // address / lat-lon fallbacks). An explicit editor override wins.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

// View Floor Plans CTA inside the mobile drawer. Hard-coded `/floor-plans`
// before; promoting to a slot group so the editor can retarget the
// button (e.g. to an off-site availability widget) and relabel it. Mirrors
// the schedule-tour link shape so the editor experience is consistent.
const viewFloorPlansLinkDef = {
  label: 'View Floor Plans Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// SVG Icon Components
// ============================================================================

function PhoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
    </svg>
  );
}

function MapPinIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

// ============================================================================
// Chevron Down Icon for "More" dropdown
// ============================================================================

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
    </svg>
  );
}

// ============================================================================
// Desktop Nav Link
// ============================================================================

function DesktopNavLink({ link }: { link: NavLink }) {
  const hasChildren = !!(link.children && link.children.length);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click-outside / Escape close — mirrors the "More" dropdown so the menu
  // dismisses cleanly when focus or pointer leaves the parent group.
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  if (!hasChildren) {
    return (
      <a
        href={link.href}
        className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 whitespace-nowrap"
      >
        {link.label}
      </a>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className="relative"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      {/* The parent label is still a real link so clicking it navigates to
          the parent page; the chevron + dropdown surface the child pages on
          hover/focus without stealing the primary affordance. */}
      <a
        href={link.href}
        className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
        aria-haspopup="true"
        aria-expanded={open}
        onFocus={() => setOpen(true)}
      >
        {link.label}
        <ChevronDownIcon
          className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </a>

      <div
        className={`nav-more-dropdown absolute left-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
          open
            ? 'opacity-100 visible translate-y-0'
            : 'opacity-0 invisible -translate-y-1'
        }`}
        style={{ backgroundColor: 'var(--vibe-color-background)' }}
      >
        {link.children!.map((child) => (
          <a
            key={child.href}
            href={child.href}
            className="rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors"
          >
            {child.label}
          </a>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// Desktop Navigation with dynamic "More" overflow dropdown
// ============================================================================

function DesktopNav({ navLinks }: { navLinks: NavLink[] }) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(navLinks.length);
  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef<HTMLDivElement>(null);

  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    const availableWidth = container.offsetWidth;
    const children = Array.from(measure.children) as HTMLElement[];
    const computedGap = parseFloat(getComputedStyle(container).gap) || 20;
    const moreButtonWidth = 90;

    let totalWidth = 0;
    let fitCount = 0;

    for (let i = 0; i < children.length; i++) {
      const childWidth = children[i].offsetWidth;
      const widthWithGap = i === 0 ? childWidth : childWidth + computedGap;
      const needed = totalWidth + widthWithGap + (i < children.length - 1 ? moreButtonWidth + computedGap : 0);

      if (needed > availableWidth && i < children.length) {
        const remainingWidth = totalWidth + widthWithGap;
        let allFit = true;
        let testWidth = remainingWidth;
        for (let j = i + 1; j < children.length; j++) {
          testWidth += children[j].offsetWidth + computedGap;
          if (testWidth > availableWidth) {
            allFit = false;
            break;
          }
        }
        if (allFit) {
          fitCount = children.length;
        } else {
          fitCount = i;
        }
        break;
      }
      totalWidth += widthWithGap;
      fitCount = i + 1;
    }

    setVisibleCount(fitCount);
  }, [navLinks.length]);

  useEffect(() => {
    recalculate();
    const observer = new ResizeObserver(() => recalculate());
    if (navContainerRef.current) {
      observer.observe(navContainerRef.current);
    }
    return () => observer.disconnect();
  }, [recalculate]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [moreOpen]);

  const visibleLinks = navLinks.slice(0, visibleCount);
  const overflowLinks = navLinks.slice(visibleCount);

  return (
    <>
      {/* Hidden measurement row. Parent links with children render with a
          chevron in the live nav, so we mirror that here so the per-link
          offsetWidth used by the overflow calculation matches the visible
          width — otherwise the chevron would push the rightmost link out
          unexpectedly when the row is near full. */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="flex items-center gap-5 absolute pointer-events-none"
        style={{ visibility: 'hidden', position: 'absolute', top: 0, left: 0, whiteSpace: 'nowrap' }}
      >
        {navLinks.map((link) => (
          <span
            key={link.href}
            className="font-body text-[13px] font-semibold tracking-wider uppercase inline-flex items-center gap-1"
          >
            {link.label}
            {link.children && link.children.length ? (
              <ChevronDownIcon className="w-3.5 h-3.5" />
            ) : null}
          </span>
        ))}
      </div>

      {/* Visible navigation */}
      <nav
        ref={navContainerRef}
        className="hidden lg:flex items-center justify-end gap-5 xl:gap-7 flex-1 min-w-0 mx-6"
        role="navigation"
        aria-label="Main navigation"
      >
        {visibleLinks.map((link) => (
          <DesktopNavLink key={link.href} link={link} />
        ))}

        {overflowLinks.length > 0 && (
          <div
            ref={moreRef}
            className="relative"
            // Mirror the DesktopNavLink hover behavior — the "More"
            // overflow dropdown opens on hover/focus just like the
            // "Floor Plans" parent dropdown so the two affordances feel
            // identical. The click handler stays as a fallback for
            // touch devices where hover doesn't fire, and the
            // click-outside listener still dismisses the menu when the
            // pointer leaves the group entirely.
            onMouseEnter={() => setMoreOpen(true)}
            onMouseLeave={() => setMoreOpen(false)}
          >
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              onFocus={() => setMoreOpen(true)}
              className="rise-nav-darken-hover font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors py-2 flex items-center gap-1 whitespace-nowrap"
              aria-expanded={moreOpen}
              aria-haspopup="true"
            >
              More
              <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform duration-200 ${moreOpen ? 'rotate-180' : ''}`} />
            </button>

            <div
              className={`nav-more-dropdown absolute right-0 top-full mt-1 border border-[var(--vibe-color-border)] shadow-lg min-w-[200px] z-[70] transition-all duration-200 ${
                moreOpen
                  ? 'opacity-100 visible translate-y-0'
                  : 'opacity-0 invisible -translate-y-1'
              }`}
              style={{ backgroundColor: 'var(--vibe-color-background)' }}
            >
              {overflowLinks.map((link) => (
                <React.Fragment key={link.href}>
                  <a
                    href={link.href}
                    className="rise-nav-darken-hover block px-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text)] transition-colors"
                  >
                    {link.label}
                  </a>
                  {link.children?.map((child) => (
                    <a
                      key={child.href}
                      href={child.href}
                      // Indent child rows so the More dropdown reads as a
                      // nested list when a parent (e.g. Floor Plans) gets
                      // pushed into overflow and we want its sub-items to
                      // remain reachable from the dropdown.
                      className="rise-nav-darken-hover block pl-9 pr-5 py-3 font-body text-[13px] font-semibold tracking-wider uppercase text-[var(--vibe-color-text-muted)] transition-colors"
                    >
                      {child.label}
                    </a>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

// ============================================================================
// Mobile Nav Link
// ============================================================================

function MobileNavLink({
  link,
  onNavigate,
}: {
  link: NavLink;
  onNavigate: () => void;
  /** Retained in callers for parity with desktop but intentionally
      unused — see comment below. Children render flush with their
      parents in the drawer because the visual offset reads as misaligned
      noise in a vertical list (the desktop hover dropdown already
      conveys the hierarchy there). */
  isChild?: boolean;
}) {
  return (
    <a
      href={link.href}
      onClick={onNavigate}
      className="rise-nav-darken-hover block py-3.5 px-6 font-body text-sm font-semibold uppercase tracking-wider text-[var(--vibe-color-text)] transition-colors border-b border-[var(--vibe-color-border)]/50"
    >
      {link.label}
    </a>
  );
}

// ============================================================================
// Main Header Component
// ============================================================================
// STRUCTURE: Single navigation bar (logo | nav links | utility + CTA).
// The old 3-bar stack (banner + utility bar + nav bar) is replaced by one
// unified bar. Utility links (phone, Apply Now, Resident Access) are
// inlined into the right side of the nav bar on desktop, and folded into
// the mobile drawer on small screens.
// ============================================================================

export function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [scrolled, setScrolled] = useState(false);
  const headerRef = useRef<HTMLElement>(null);

  // Scroll detection for transparent → solid transition
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Resolve the current page synchronously via the runtime context (works at
  // SSG time, no flash-of-white on hydration). Fall back to the URL pathname
  // for any oddball preview/dev path that isn't wired through usePage().
  const pageCtx = usePage();
  const overSolidHero = useMemo(
    () =>
      isOverSolidHero(
        pageCtx?.alias,
        typeof window !== 'undefined' ? window.location.pathname : pageCtx?.path,
      ),
    [pageCtx?.alias, pageCtx?.path],
  );

  // Pick the more readable text color for the transparent nav when it sits on
  // top of `HeroSolidBG` (which paints the section in `--vibe-color-primary`).
  // Uses the same `getContrastTextColor` helper the Footer uses (and that the
  // tokens runtime uses to compute `--vibe-color-text-on-primary`), so the
  // nav, the hero CTA, and the footer all pick the same contrast side on a
  // given primary.
  //
  // The earlier implementation here did `pickHighestContrast(primary,
  // [textOnPrimary, textOnSecondary])`, which was technically WCAG-optimal
  // but produced black text on medium-tone primaries (gold, tan, dusty
  // blue): on a primary with luminance ~0.3, dark-on-medium scores ~5:1 vs.
  // white-on-medium ~3:1, so the picker chose dark — disagreeing with the
  // runtime's own text-on-primary token and rendering muddy black nav links
  // on what should be a white-text design. The shared helper applies the
  // documented 0.55 threshold that favors white on those medium tones.
  const tokens = useTokens();
  const navOverHeroColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return null;
    return getContrastTextColor(primary);
  }, [tokens]);

  // CTA accent color for the SCROLLED (white-background) nav state. The
  // outline-style `Schedule a Tour` button paints its border / label in this
  // color. When the property's primary is too pale to read against the white
  // header (contrast < WCAG AA 4.5:1), fall back to the dark `text` token so
  // the CTA stays legible after the transparent → solid header transition.
  // We also expose a paired "on-accent" color for the hover fill state — the
  // runtime keeps `textOnPrimary` calibrated to primary, so when we switch
  // the fill to `text` we have to pair it with `background` (white) instead.
  const [navCtaAccent, navCtaOnAccent] = useMemo(() => {
    const primary = tokens?.color?.primary;
    const background = tokens?.color?.background || '#ffffff';
    if (!primary) return ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const;
    return contrastRatio(primary, background) >= 4.5
      ? ['var(--vibe-color-primary)', 'var(--vibe-color-text-on-primary)'] as const
      : ['var(--vibe-color-text)', 'var(--vibe-color-background)'] as const;
  }, [tokens]);

  // Virtual tours — controls nav-link visibility
  const virtualTours = useCollectionBinding<VirtualTour>('header.virtual_tours', { collection: 'virtual_tours' });
  const hasVirtualTours = (virtualTours || []).some((t) => t.virtual_tour_url);

  // Interactive Map — drops out of the nav unless the property is wired to
  // one of the two supported interactive site-map providers:
  //
  //   • Engrain SightMap → `custom_engrain_sightmap_url`
  //   • ApartmentSync    → `custom_apartmentsync_javascript_id`
  //
  // The provider gate is satisfied when EITHER field is present and non-empty
  // (a property configured with either provider has a real map to render); a
  // property with neither has no map and shouldn't surface the page. Both are
  // custom property fields (typed as `text`), so we read them via
  // `useProperty()` directly rather than the strongly-typed `usePropertyField`
  // (which only knows the built-in scalar fields).
  //
  // This provider gate is ANDed with the page's own `visible` flag in
  // `buildNavLinks` — the page must be enabled on the (child) pages.json AND
  // have a provider configured before it appears in the nav.
  const propertyForLeasing = useProperty() as Record<string, unknown>;
  const engrainSightmapUrl = propertyForLeasing?.custom_engrain_sightmap_url as
    | string
    | null
    | undefined;
  const apartmentSyncId = propertyForLeasing?.custom_apartmentsync_javascript_id as
    | string
    | null
    | undefined;
  const hasInteractiveLeasing = !!(
    (engrainSightmapUrl && String(engrainSightmapUrl).trim()) ||
    (apartmentSyncId && String(apartmentSyncId).trim())
  );

  const navLinks = useMemo(
    () => buildNavLinks(hasVirtualTours, hasInteractiveLeasing),
    [hasVirtualTours, hasInteractiveLeasing],
  );

  // Content tokens
  const phone = useContentToken('header.phone');

  // Slot groups
  const logoRaw = useSlotGroup('header.logo', logoSlotDef);
  const monoWhiteLogo = usePropertyField('property_mono_white_logo');
  const monoBlackLogo = usePropertyField('property_mono_black_logo');

  // Logo color choice mirrors the nav text color logic:
  //   • Scrolled (solid white --vibe-color-background) → default to
  //     mono_black so the logo always reads against the white header bar.
  //     Falls back to whatever the editor picked (logoRaw.image, typically
  //     the primary full-color logo) if the property has no mono_black
  //     uploaded.
  //   • Transparent over HeroSolidBG (interior page) → match the picked
  //     on-color token: light text → mono_white, dark text → mono_black.
  //   • Transparent over photographic Hero (home) → keep mono_white as
  //     before (white reads against the dark gradient overlay).
  const wantsDarkOnHero = !!navOverHeroColor && relativeLuminance(navOverHeroColor) < 0.5;
  const logoImage = scrolled
    ? (monoBlackLogo || logoRaw.image)
    : overSolidHero && wantsDarkOnHero
      ? (monoBlackLogo || logoRaw.image)
      : (monoWhiteLogo || logoRaw.image);
  const logo = { ...logoRaw, image: logoImage };

  const applyNowLink = useSlotGroup('header.apply_now_link', applyNowLinkDef);
  const residentAccessLink = useSlotGroup('header.resident_access_link', residentAccessLinkDef);
  const qualifyingCriteriaLink = useSlotGroup(
    'header.qualifying_criteria_link',
    qualifyingCriteriaLinkDef,
  );
  const scheduleTourLink = useSlotGroup('header.schedule_tour_link', scheduleTourLinkDef);
  const addressLink = useSlotGroup('header.address_link', addressLinkDef);
  // Fallback URL when the editor hasn't set a custom address link — hook
  // builds a mobile-safe Google Maps URL from property place / business /
  // full-address / lat-lon fields, in that order.
  const propertyAddressUrl = usePropertyAddressUrl();
  const viewFloorPlansLink = useSlotGroup('header.view_floor_plans_link', viewFloorPlansLinkDef);

  // Google Fonts
  useEffect(() => {
    if (document.querySelector('link[data-vibe-font="lexend-deca"]')) return;
    const preconnect1 = document.createElement('link');
    preconnect1.rel = 'preconnect';
    preconnect1.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect1);
    const preconnect2 = document.createElement('link');
    preconnect2.rel = 'preconnect';
    preconnect2.href = 'https://fonts.gstatic.com';
    preconnect2.crossOrigin = 'anonymous';
    document.head.appendChild(preconnect2);
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Lexend+Deca:wght@300;400;500;600;700&display=swap';
    link.setAttribute('data-vibe-font', 'lexend-deca');
    document.head.appendChild(link);
  }, []);

  // Measure header height for spacer
  const updateHeaderHeight = useCallback(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  useEffect(() => {
    updateHeaderHeight();
    const observer = new ResizeObserver(updateHeaderHeight);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    return () => observer.disconnect();
  }, [updateHeaderHeight]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const closeMobileMenu = () => setMenuOpen(false);

  return (
    <>
      {/* No spacer — transparent header overlays hero content */}

      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-50 w-full transition-all duration-500 ease-in-out${scrolled ? ' header-scrolled' : ' bg-transparent header-transparent'}${overSolidHero ? ' header-over-solid-hero' : ''}`}
        style={{
          top: 0,
          transition: 'background-color 0.5s ease',
          backgroundColor: scrolled ? 'var(--vibe-color-background)' : undefined,
          // Luminance-picked text color for the transparent nav over a
          // `HeroSolidBG` (primary-color) section. Falls back to white via the
          // CSS rules below when this var is absent (e.g. home page with the
          // photographic hero, or before tokens have hydrated).
          ...(navOverHeroColor ? { ['--rise-nav-over-hero-text' as string]: navOverHeroColor } : {}),
          // Luminance-picked CTA accent for the scrolled / white-background
          // nav state. Stays as primary when the brand color is dark enough,
          // otherwise flips to the theme `text` color so the outline button
          // doesn't disappear into the white header bar. The paired
          // on-accent var drives the hover label color so it stays readable
          // against the new fill.
          ['--rise-nav-cta-accent' as string]: navCtaAccent,
          ['--rise-nav-cta-on-accent' as string]: navCtaOnAccent,
        }}
      >
        {/* ============================================================== */}
        {/* Single unified navigation bar                                  */}
        {/* ============================================================== */}
        <div
          className="header-bar"
          style={{ paddingTop: 'var(--offer-banner-height, 0px)', transition: 'padding-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
        >
          {/* ============================================================ */}
          {/* Utility row — desktop only. Sits between the SpecialOfferBanner
              (which adds its height to the header-bar's paddingTop above)
              and the main nav row below. Hosts the phone, Apply Now, and
              Resident Access links that previously lived inline in the main
              nav's right group. The links remain available on mobile via
              the drawer further down — we don't surface a second row on
              small screens because vertical space matters more there.
              `header-util-row` paints a thin bottom border so the row
              reads as visually separate from the main nav; the border
              color follows the transparent/scrolled state via the
              overrides in the <style> block. */}
          {/* ============================================================ */}
          <div className="header-util-row hidden lg:block">
            <div className="px-4 sm:px-6 lg:px-8">
              {/* Two-group layout: phone + address sit on the left edge,
                  Apply Now + Resident Access stay on the right. `justify-
                  between` pushes the groups to opposite edges; each group is
                  its own flex row so the inner dividers + gaps behave the
                  same as before. */}
              <div className="flex items-center justify-between gap-1 h-9">
                {/* Left-aligned group: phone + address */}
                <div className="flex items-center gap-1">
                  {/* Phone */}
                  <a
                    href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                    className="dniphone dniphonehref header-util-link"
                    aria-label="Call us"
                  >
                    <PhoneIcon className="w-3.5 h-3.5 flex-shrink-0" />
                    <EditableToken
                      path="header.phone"
                      defaultField="property_call_tracking_phone"
                      defaultValue="(555) 555-5555"
                      as="span"
                    >
                      {formatPhone(phone)}
                    </EditableToken>
                  </a>

                  <span className="header-util-divider" aria-hidden="true" />

                  {/* Address — shares the existing `header.address_link`
                      slot group with the mobile drawer below, so editors
                      see one address+URL pair that drives both surfaces.
                      URL defaults to the property's Google Place URL
                      (opens Maps directly), falls back to '#' if unset. */}
                  <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                    <a
                      href={addressLink.url || propertyAddressUrl || '#'}
                      target={addressLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="header-util-link"
                      aria-label="View on Google Maps"
                    >
                      <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span>{addressLink.address}</span>
                    </a>
                  </EditableSlotGroup>
                </div>

                {/* Right-aligned group: utility links. Source order is
                    [Qualifying Criteria, div, Apply Now, div, Resident
                    Access]; the Via6 Header.tsx wrapper DOM-swaps Apply
                    Now and Resident Access in place after mount so the
                    visible row reads [Qualifying Criteria | Residents |
                    Apply Now] — Qualifying Criteria stays leftmost. */}
                <div className="flex items-center gap-1">
                  {/* Qualifying Criteria — external PDF, opens in a new tab. */}
                  <EditableSlotGroup
                    groupPath="header.qualifying_criteria_link"
                    definition={qualifyingCriteriaLinkDef}
                  >
                    <a
                      href={qualifyingCriteriaLink.url || '#'}
                      target={qualifyingCriteriaLink._target || '_blank'}
                      rel="noopener noreferrer"
                      className="header-util-link"
                    >
                      {qualifyingCriteriaLink.label || 'Qualifying Criteria'}
                    </a>
                  </EditableSlotGroup>

                  <span className="header-util-divider" aria-hidden="true" />

                  {/* Apply Now */}
                  <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                    <a
                      href={applyNowLink.url || '/apply-now'}
                      target={applyNowLink._target || '_self'}
                      className="header-util-link"
                    >
                      {applyNowLink.label || 'Apply Now'}
                    </a>
                  </EditableSlotGroup>

                  <span className="header-util-divider" aria-hidden="true" />

                  {/* Resident Access */}
                  <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                    <a
                      href={residentAccessLink.url || '/resident-access'}
                      target={residentAccessLink._target || '_self'}
                      className="header-util-link"
                    >
                      {residentAccessLink.label || 'Resident Access'}
                    </a>
                  </EditableSlotGroup>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex items-center h-16 lg:h-[72px]">
              {/* Logo */}
              <EditableSlotGroup groupPath="header.logo" definition={logoSlotDef}>
                <a href="/" className="flex-shrink-0">
                  {logo.image ? (
                    <VibesitesImage
                      url={logo.image}
                      alt={logo.alt || 'Community Logo'}
                      className="h-10 lg:h-12 w-auto object-contain"
                    />
                  ) : (
                    <div className="font-heading text-xl lg:text-2xl font-bold text-[var(--vibe-color-primary)]">
                      Via6
                    </div>
                  )}
                </a>
              </EditableSlotGroup>

              {/* Desktop Navigation Links */}
              <DesktopNav navLinks={navLinks} />

              {/* Right group — CTA + hamburger only. Phone / Apply Now /
                  Resident Access moved up to the new utility row above so
                  the main nav stays focused on page links + the primary
                  Schedule-a-Tour CTA. */}
              <div className="flex items-center gap-3 flex-shrink-0 ml-auto">
                {/* Schedule Tour CTA */}
                <EditableSlotGroup groupPath="header.schedule_tour_link" definition={scheduleTourLinkDef}>
                  <a
                    href={scheduleTourLink.url || '/schedule-a-tour'}
                    target={scheduleTourLink._target || '_self'}
                    className="nav-cta-btn group/cta relative inline-flex items-center gap-2 px-4 py-2.5 lg:px-6 lg:py-3 text-[10px] lg:text-[11px] font-medium tracking-[0.15em] uppercase overflow-hidden transition-all duration-300"
                  >
                    <span className="relative z-10">
                      {scheduleTourLink.label || 'Schedule a Tour'}
                    </span>
                  </a>
                </EditableSlotGroup>

                {/* Mobile Hamburger */}
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="lg:hidden relative w-10 h-10 flex items-center justify-center"
                  aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={menuOpen}
                >
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'rotate-45' : '-translate-y-2'
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? 'opacity-0' : ''
                    }`}
                  />
                  <span
                    className={`absolute block w-6 h-0.5 bg-[var(--vibe-color-text)] transition-all duration-300 ${
                      menuOpen ? '-rotate-45' : 'translate-y-2'
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ============================================================== */}
        {/* Mobile Menu Overlay                                            */}
        {/* ============================================================== */}
        <div
          className={`mobile-menu-panel lg:hidden fixed inset-x-0 bottom-0 overflow-y-auto transition-all duration-300 z-[61] ${
            menuOpen
              ? 'opacity-100 visible'
              : 'opacity-0 invisible pointer-events-none'
          }`}
          style={{ top: `${headerHeight}px`, backgroundColor: 'var(--vibe-color-background)' }}
        >
          {/* Navigation links */}
          <nav role="navigation" aria-label="Mobile navigation">
            {navLinks.map((link) => (
              <React.Fragment key={link.href}>
                <MobileNavLink link={link} onNavigate={closeMobileMenu} />
                {link.children?.map((child) => (
                  <MobileNavLink
                    key={child.href}
                    link={child}
                    onNavigate={closeMobileMenu}
                    isChild
                  />
                ))}
              </React.Fragment>
            ))}
          </nav>

          {/* View Floor Plans CTA — distinct from the top-bar Schedule-a-Tour
              CTA so the drawer surfaces a different primary action. Uses the
              same `mobile-util-pill` outline-fill-on-hover treatment as the
              Apply Now / Resident Access pills below so all three drawer
              CTAs share a single visual language (primary-color border +
              label, primary-color fill on hover with text-on-primary label).
              Wrapped in EditableSlotGroup so the editor can target it from
              inside the open drawer (url / label / target). */}
          <div className="p-5">
            <EditableSlotGroup groupPath="header.view_floor_plans_link" definition={viewFloorPlansLinkDef}>
              <a
                href={viewFloorPlansLink.url || '/floor-plans'}
                target={viewFloorPlansLink._target || '_self'}
                onClick={closeMobileMenu}
                className="mobile-util-pill block text-center w-full py-3.5 text-sm font-medium tracking-[0.15em] uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
              >
                {viewFloorPlansLink.label || 'View Floor Plans'}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Utility CTA Buttons — wrapped in EditableSlotGroup so the
              editor can target them from inside the open mobile drawer
              the same way it can in the desktop nav above. The wrappers
              are display:contents-ish in practice (they don't introduce
              visible chrome), so the two-column grid layout below still
              treats each anchor as a direct grid item. Qualifying Criteria
              is rendered above the grid as a full-width pill (matching the
              View Floor Plans treatment above) so the three pills read as
              View Floor Plans → Qualifying Criteria → [Residents | Apply
              Now], which mirrors the desktop utility row's leftmost-first
              ordering on the smaller surface. */}
          <div className="px-5 pb-3">
            <EditableSlotGroup
              groupPath="header.qualifying_criteria_link"
              definition={qualifyingCriteriaLinkDef}
            >
              <a
                href={qualifyingCriteriaLink.url || '#'}
                target={qualifyingCriteriaLink._target || '_blank'}
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="mobile-util-pill block text-center w-full mb-2 py-2.5 text-[11px] font-medium tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
              >
                {qualifyingCriteriaLink.label || 'Qualifying Criteria'}
              </a>
            </EditableSlotGroup>
            <div className="grid grid-cols-2 gap-2">
              <EditableSlotGroup groupPath="header.apply_now_link" definition={applyNowLinkDef}>
                <a
                  href={applyNowLink.url || '/apply-now'}
                  target={applyNowLink._target || '_self'}
                  className="mobile-util-pill block text-center py-2.5 text-[11px] font-medium tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
                >
                  {applyNowLink.label || 'Apply Now'}
                </a>
              </EditableSlotGroup>
              <EditableSlotGroup groupPath="header.resident_access_link" definition={residentAccessLinkDef}>
                <a
                  href={residentAccessLink.url || '/resident-access'}
                  target={residentAccessLink._target || '_self'}
                  className="mobile-util-pill block text-center py-2.5 text-[11px] font-medium tracking-wider uppercase border border-[var(--vibe-color-primary)] text-[var(--vibe-color-primary)] hover:bg-[var(--vibe-color-primary)] transition-all"
                >
                  {residentAccessLink.label || 'Resident Access'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          {/* Address & Phone */}
          <div className="px-5 pb-6 pt-3 border-t border-[var(--vibe-color-border)]/50">
            <div className="space-y-3">
              <EditableSlotGroup groupPath="header.address_link" definition={addressLinkDef}>
                <a
                  href={addressLink.url || propertyAddressUrl || '#'}
                  target={addressLink._target || '_blank'}
                  rel="noopener noreferrer"
                  className="flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
                >
                  <MapPinIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                  <span className="rise-nav-darken-hover">{addressLink.address}</span>
                </a>
              </EditableSlotGroup>
              <a
                href={`tel:${phone?.replace(/[^+\d]/g, '')}`}
                className="dniphone dniphonehref flex items-center gap-2.5 text-sm text-[var(--vibe-color-text-muted)] hover:text-[var(--vibe-color-primary)] transition-colors"
              >
                <PhoneIcon className="w-4 h-4 text-[var(--vibe-color-primary)] flex-shrink-0" />
                {/* Mirror the desktop phone treatment: EditableToken
                    wraps just the inner text span so the editor can
                    target the phone number from inside the open mobile
                    drawer. The outer <a> still routes to `tel:` for
                    real visitors. */}
                <EditableToken
                  path="header.phone"
                  defaultField="property_call_tracking_phone"
                  defaultValue="(555) 555-5555"
                  as="span"
                  className="rise-nav-darken-hover"
                >
                  {formatPhone(phone)}
                </EditableToken>
              </a>
            </div>
          </div>
        </div>
      </header>

      <style>{`
        /* ================================================================ */
        /* Single navigation bar                                           */
        /* ================================================================ */
        .header-bar {
          /* Match the background-color transition curve so border + shadow
             fade in/out in lock-step with the transparent → solid swap. */
          border-bottom: 1px solid transparent;
          transition:
            border-color 0.5s ease,
            box-shadow 0.5s ease;
        }
        .header-scrolled .header-bar {
          border-bottom-color: var(--vibe-color-border);
          box-shadow:
            0 1px 3px rgba(0,0,0,0.04),
            0 4px 12px rgba(0,0,0,0.03);
        }
        .header-transparent .header-bar {
          border-bottom-color: transparent;
          box-shadow: none;
        }

        /* Transparent header: nav links → white (header bar only — NOT inside the mobile menu panel) */
        .header-transparent .header-bar a.font-body,
        .header-transparent .header-bar nav a,
        .header-transparent .header-bar button.font-body {
          color: rgba(255,255,255,0.92) !important;
        }
        .header-transparent .header-bar nav a:hover,
        .header-transparent .header-bar button.font-body:hover {
          color: #fff !important;
        }

        /* Interior pages (transparent nav over HeroSolidBG / primary color):
           swap white for the luminance-picked on-color token (textOnPrimary
           vs textOnSecondary, whichever has higher WCAG contrast against the
           primary background). The variable is set inline on the header. */
        .header-transparent.header-over-solid-hero .header-bar a.font-body,
        .header-transparent.header-over-solid-hero .header-bar nav a,
        .header-transparent.header-over-solid-hero .header-bar button.font-body {
          color: var(--rise-nav-over-hero-text, rgba(255,255,255,0.92)) !important;
        }
        .header-transparent.header-over-solid-hero .header-bar nav a:hover,
        .header-transparent.header-over-solid-hero .header-bar button.font-body:hover {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* "More" dropdown panel sits on a theme-background (white) surface,
           so its links use theme text color regardless of the header bar's
           transparent state above. The dropdown is a white-background
           context, so the hover affordance is the animated underline from
           .rise-nav-darken-hover below — we keep the text color steady
           on hover (still !important to override the parent
           .header-transparent .header-bar nav a:hover white rule).

           The .header-over-solid-hero variants must be listed explicitly:
           .header-transparent.header-over-solid-hero .header-bar nav a has
           higher specificity (0,4,1) than the plain .header-transparent
           .header-bar .nav-more-dropdown a override (0,3,1) and would
           otherwise force the dropdown links to the picked on-hero color
           (white) — producing white-on-white text inside the dropdown on
           interior pages like Floor Plans. */
        .header-bar .nav-more-dropdown a,
        .header-transparent .header-bar .nav-more-dropdown a,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a {
          color: var(--vibe-color-text) !important;
        }
        .header-bar .nav-more-dropdown a:hover,
        .header-transparent .header-bar .nav-more-dropdown a:hover,
        .header-transparent.header-over-solid-hero .header-bar .nav-more-dropdown a:hover {
          color: var(--vibe-color-text) !important;
        }

        /* Transparent header: utility links → white */
        .header-transparent .header-util-link {
          color: rgba(255,255,255,0.85);
        }
        .header-transparent .header-util-link:hover {
          color: #fff;
        }
        .header-transparent .header-util-divider {
          background: rgba(255,255,255,0.3);
        }

        /* Interior pages: utility links + divider use the picked on-color
           token instead of hardcoded white. We dim the base state via
           color-mix() to match the ~85% / 30% feel of the photographic
           variant above. */
        .header-transparent.header-over-solid-hero .header-util-link {
          color: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 85%, transparent);
        }
        .header-transparent.header-over-solid-hero .header-util-link:hover {
          color: var(--rise-nav-over-hero-text, #fff);
        }
        .header-transparent.header-over-solid-hero .header-util-divider {
          background: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 30%, transparent);
        }

        /* Mobile menu panel uses --vibe-color-background (white), so links
           must render in theme text color regardless of the header bar's
           transparent / scrolled state above it. The hover affordance for
           nav links is the animated underline from .rise-nav-darken-hover
           below, so we hold text color steady on hover here too. */
        .mobile-menu-panel a,
        .mobile-menu-panel button {
          color: var(--vibe-color-text);
        }
        .mobile-menu-panel a:hover {
          color: var(--vibe-color-text);
        }

        /* Nav links: animated underline on hover (used by Header
           DesktopNavLink, "More" dropdown items, MobileNavLink, and the
           Navigation sub-bar). All of these contexts render against a
           white surface, so the affordance is an underline rather than a
           color shift — the underline is drawn via text-decoration so it
           rides the text baseline and works at any link padding. The
           transparent-header (over-hero) brightening hover above keeps
           its own color shift via earlier !important rules; the
           underline simply layers on top with currentColor. */
        .rise-nav-darken-hover {
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 6px;
          transition: text-decoration-color 0.25s ease, text-underline-offset 0.25s ease, color 0.2s ease;
        }
        .rise-nav-darken-hover:hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Address / phone rows in the mobile drawer wear the
           rise-nav-darken-hover class on the inner text span (not the link
           itself, so the underline doesn't run under the icon). Trigger
           the underline from the parent link's :hover/:focus-visible so
           the whole row remains the hover target. */
        .mobile-menu-panel a:hover .rise-nav-darken-hover,
        .mobile-menu-panel a:focus-visible .rise-nav-darken-hover {
          text-decoration-color: currentColor;
          text-underline-offset: 4px;
        }
        /* Outline pills inside the drawer flip to text-on-primary on hover. */
        .mobile-menu-panel .mobile-util-pill:hover {
          color: var(--vibe-color-text-on-primary);
        }

        /* Transparent header: hamburger bars → white */
        .header-transparent span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: #fff !important;
        }

        /* Transparent header: site name fallback text */
        .header-transparent .font-heading {
          color: #fff !important;
        }

        /* Interior pages: hamburger bars + site-name fallback also flip to
           the luminance-picked on-color token. */
        .header-transparent.header-over-solid-hero span[class*="bg-[var(--vibe-color-text)]"] {
          background-color: var(--rise-nav-over-hero-text, #fff) !important;
        }
        .header-transparent.header-over-solid-hero .font-heading {
          color: var(--rise-nav-over-hero-text, #fff) !important;
        }

        /* ================================================================ */
        /* Utility row — sits above the main nav row inside .header-bar.   */
        /* ----------------------------------------------------------------- */
        /* Subtle divider line between the utility row and the main nav row
           when the header is in its scrolled (white-background) state. The
           transparent/over-hero variants below soften the divider to a
           translucent tint of the picked on-color so it still reads against
           dark hero photography or the solid primary section without
           painting a hard line. */
        .header-util-row {
          border-bottom: 1px solid transparent;
          transition: border-color 0.5s ease;
        }
        .header-scrolled .header-util-row {
          border-bottom-color: var(--vibe-color-border);
        }
        .header-transparent .header-util-row {
          border-bottom-color: rgba(255, 255, 255, 0.18);
        }
        .header-transparent.header-over-solid-hero .header-util-row {
          border-bottom-color: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 18%, transparent);
        }

        /* ================================================================ */
        /* Inline utility links (phone, apply, resident)                   */
        /* ================================================================ */
        .header-util-link {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.25rem 0.5rem;
          font-family: var(--vibe-font-body);
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--vibe-color-text-muted);
          /* Animated underline on hover — mirrors the .rise-nav-darken-hover
             treatment used by the main nav links so phone / address / apply
             now / resident access share the same hover language. The
             underline is drawn via text-decoration so it rides the text
             baseline and fades cleanly between transparent and currentColor.
             text-underline-offset is tighter (3px) than the nav links (6px →
             4px) because the utility row's row-height is smaller. */
          text-decoration-line: underline;
          text-decoration-color: transparent;
          text-decoration-thickness: 1.5px;
          text-underline-offset: 5px;
          white-space: nowrap;
          transition: color 0.2s ease, text-decoration-color 0.25s ease, text-underline-offset 0.25s ease;
        }
        /* White-background hover matches the nav links' darken-25% rule.
           The .header-transparent and .header-over-solid-hero overrides
           below carry higher specificity so they still win when the
           header sits on top of a hero. */
        .header-util-link:hover {
          color: color-mix(in srgb, var(--vibe-color-primary), black 25%);
          text-decoration-color: currentColor;
          text-underline-offset: 3px;
        }
        .header-util-divider {
          display: block;
          width: 1px;
          height: 14px;
          background: var(--vibe-color-border);
          flex-shrink: 0;
        }

        /* ================================================================ */
        /* CTA Button — transparent / outline style                        */
        /* ----------------------------------------------------------------- */
        /* Border + label use --rise-nav-cta-accent (luminance-picked).
           That variable resolves to --vibe-color-primary when the property's
           primary has WCAG AA contrast against the white scrolled header,
           and falls back to --vibe-color-text (the dark theme color) for
           pale primaries that would otherwise wash out on white. The hover
           fill follows the same accent so the swap is consistent. */
        .nav-cta-btn {
          background: transparent;
          border: 1.5px solid var(--rise-nav-cta-accent, var(--vibe-color-primary));
          border-radius: 2px;
          color: var(--rise-nav-cta-accent, var(--vibe-color-primary));
          box-shadow: none;
        }
        .nav-cta-btn::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--rise-nav-cta-accent, var(--vibe-color-primary));
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .nav-cta-btn:hover {
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--rise-nav-cta-accent, var(--vibe-color-primary)) 25%, transparent);
          transform: translateY(-1px);
          color: var(--rise-nav-cta-on-accent, var(--vibe-color-text-on-primary));
        }
        .nav-cta-btn:hover::before {
          opacity: 1;
        }
        .nav-cta-btn:active {
          transform: translateY(0);
          box-shadow: none;
        }

        /* When header is transparent (over hero) — white border & text */
        .header-transparent .nav-cta-btn {
          border-color: rgba(255,255,255,0.75);
          color: #fff;
        }
        .header-transparent .nav-cta-btn::before {
          background: rgba(255,255,255,0.18);
        }
        .header-transparent .nav-cta-btn:hover {
          border-color: #fff;
          color: #fff;
          box-shadow: none;
        }

        /* Interior pages: CTA border / text follow the luminance-picked
           on-color token. The translucent fill that slides in on hover uses
           color-mix() so it stays in palette when the picked color is dark. */
        .header-transparent.header-over-solid-hero .nav-cta-btn {
          border-color: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 75%, transparent);
          color: var(--rise-nav-over-hero-text, #fff);
        }
        .header-transparent.header-over-solid-hero .nav-cta-btn::before {
          background: color-mix(in srgb, var(--rise-nav-over-hero-text, #fff) 18%, transparent);
        }
        .header-transparent.header-over-solid-hero .nav-cta-btn:hover {
          border-color: var(--rise-nav-over-hero-text, #fff);
          color: var(--rise-nav-over-hero-text, #fff);
          box-shadow: none;
        }

      `}</style>
    </>
  );
}

// ============================================================================
// Content Schema
// ============================================================================

export const contentSchema: ComponentContentSchema = {
  slot: 'Header',
  displayName: 'Header',
  tokens: {
    'header.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
  },
  slotGroups: {
    'header.logo': {
      label: 'Logo',
      fields: {
        image: { label: 'Logo Image', type: 'image', defaultField: 'property_primary_logo', suggestedFields: ['property_primary_logo', 'property_mono_black_logo', 'property_secondary_logo'] },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
      },
    },
    'header.apply_now_link': {
      label: 'Apply Now Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultField: 'property_apply_now_url',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Apply Now' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.resident_access_link': {
      label: 'Resident Access Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/resident-access' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Resident Access' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.qualifying_criteria_link': {
      label: 'Qualifying Criteria Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue:
            'https://irp.cdn-website.com/c035667c/files/uploaded/Weidner_Qualifying_Criteria_Seattle.pdf',
        },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Qualifying Criteria' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.schedule_tour_link': {
      label: 'Schedule Tour Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'Schedule a Tour' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
    'header.address_link': {
      label: 'Address Link',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street, Atlanta, GA 30313',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: {
          label: 'URL',
          type: 'url',
        },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'header.view_floor_plans_link': {
      label: 'View Floor Plans Link',
      fields: {
        url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
        label: { label: 'Link Text', type: 'text', defaultValue: 'View Floor Plans' },
        _target: { label: 'Target', type: 'text', defaultValue: '_self' },
      },
    },
  },
  collectionBindings: {
    'header.virtual_tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(Header as any).contentSchema = contentSchema;

export default Header;
